import {
  getNumberOfChildrenWithPercentageValues,
  getNumberOfChildrenTreatedAsAdults,
  getChildrenSelectedCategories
} from '../screen/accomodation/component/RatePlans/SingleRatePlan/helper/getTotalRate'
import getAllAdultsRate, {
  capChildrenPrice
} from '../screen/accomodation/component/RatePlans/SingleRatePlan/helper/getAllAdultsRate'
import childrenCategoriesType from 'Rentlio/utils/childrenCategoriesEnum'
import childrenCategoryDerivationType from 'Rentlio/utils/childrenCategoryDerivationTypeEnum'
import { PROMO_CODE_DISCOUNT_TYPE_ENUM } from 'Rentlio/utils/promoCodeDiscountTypeEnum'
import { getTouristTax } from 'Rentlio/components/ReservationSummary/util/getTouristTax'

const getFirstNightPrice = (unitTypeOccupancy, ratePlan, adults, childrenAges) => {
  if (!ratePlan) {
    return 0
  }

  let firstNightPrice = 0

  if (ratePlan.isPaxRate) {
    firstNightPrice = getOBPRateFirstNightPrice(ratePlan, adults, childrenAges, unitTypeOccupancy)
  } else if (ratePlan.isPPPDRate) {
    firstNightPrice = getPPPDRateFirstNightPrice(ratePlan, adults, childrenAges)
  } else {
    firstNightPrice = ratePlan.firstNightBasePrice
  }
  if (ratePlan.activePromoCode) {
    if (ratePlan.activePromoCode?.discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.PERCENTAGE) {
      const firstNightIncludedTouristTax = ratePlan.isTouristTaxIncluded
        ? getTouristTax([ratePlan.includedTouristTaxes[0]], adults, childrenAges)
        : 0
      return (
        (firstNightPrice - firstNightIncludedTouristTax) *
          (1 - Number(ratePlan.activePromoCode?.discountAmount) / 100) +
        firstNightIncludedTouristTax
      )
    }

    if (ratePlan.activePromoCode?.discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.NOMINAL) {
      const firstNightIncludedTouristTax = ratePlan.isTouristTaxIncluded
        ? getTouristTax([ratePlan.includedTouristTaxes[0]], adults, childrenAges)
        : 0
      const discountByNight = Number(ratePlan.activePromoCode?.discountAmount) / Number(ratePlan.nights)

      // firstNightIncludedTouristTax must not be included in disclunt, so if discountByNight is greater than firstNightPrice
      // we will return only the first night tourist tax
      return (
        Math.max(firstNightPrice - firstNightIncludedTouristTax - discountByNight, 0) + firstNightIncludedTouristTax
      )
    }
  }

  return firstNightPrice
}

const getOBPRateFirstNightPrice = (ratePlan, adults, childrenAges, unitTypeOccupancy) => {
  const { childrenCategories } = ratePlan
  let adultsNumber = adults || 0
  const childrenSelectedCategories = getChildrenSelectedCategories(childrenAges, childrenCategories)
  // because value type nominal for children we are not counting inside pax, we are adding it separately
  const numberOfChildrenTreatedAsAdults = getNumberOfChildrenTreatedAsAdults(childrenSelectedCategories)
  adultsNumber += numberOfChildrenTreatedAsAdults

  const persons = adultsNumber + getNumberOfChildrenWithPercentageValues(childrenSelectedCategories)

  const paxPriceForAllPersons = getPaxForPersons(ratePlan, persons)
    ? getPaxForPersons(ratePlan, persons).firstNightBasePrice
    : 0
  const paxPriceForAdults = getPaxForPersons(ratePlan, adultsNumber)
    ? getPaxForPersons(ratePlan, adultsNumber).firstNightBasePrice
    : 0

  const childrenPrice = getOBPChildrenPrice(
    childrenSelectedCategories,
    (paxPriceForAllPersons - paxPriceForAdults) / (persons - adultsNumber)
  )

  const allAdultsPrice = getAllAdultsRate(ratePlan, unitTypeOccupancy, adults, childrenAges, 'firstNightBasePrice')

  return paxPriceForAdults + capChildrenPrice(childrenPrice, paxPriceForAdults, allAdultsPrice)
}

const getPPPDRateFirstNightPrice = (
  { firstNightBasePrice, pricePerPerson, childrenCategories },
  adults,
  childrenAges
) => {
  const childrenSelectedCategories = getChildrenSelectedCategories(childrenAges, childrenCategories)
  const childrenPrice = getPPPDChildrenPrice(childrenSelectedCategories, pricePerPerson)

  const adultsNumber = adults || 0
  const adultPrice = adultsNumber * pricePerPerson
  return firstNightBasePrice + adultPrice + childrenPrice
}

const getPPPDChildrenPrice = (categories, priceForSingleAdultNight) => {
  if (!categories) {
    return 0
  }

  let priceForChildren = 0

  categories.map(category => {
    switch (category.type) {
      case childrenCategoriesType.AS_ADULT:
        priceForChildren = priceForChildren + priceForSingleAdultNight
        break
      case childrenCategoriesType.CUSTOM: {
        if (!category.percentageValue && !category.nominalValue) {
          break
        }
        const priceForChild =
          category.valueType === childrenCategoryDerivationType.PERCENTAGE
            ? (+category.percentageValue / 100) * priceForSingleAdultNight
            : +category.nominalValue
        priceForChildren = priceForChildren + priceForChild
        break
      }
      case childrenCategoriesType.MANUAL:
        priceForChildren += category.nominalValue
        break
    }
  })

  return priceForChildren
}

const getPaxForPersons = (ratePlan, persons) => {
  const { paxValues } = ratePlan
  if (!persons || !paxValues) return null
  const paxValue = paxValues.find(paxValue => {
    return paxValue.pax === persons
  })
  // if persons === base pax persons then we are returning rate plans total rate - base rate
  return paxValue || { firstNightBasePrice: ratePlan.firstNightBasePrice }
}

const getOBPChildrenPrice = (categories, priceForAdult) => {
  if (!categories || categories.length === 0) {
    return 0
  }
  let priceForChildren = 0

  categories.map(category => {
    switch (category.type) {
      case childrenCategoriesType.AS_ADULT:
        break
      case childrenCategoriesType.CUSTOM: {
        if (!category.percentageValue && !category.nominalValue) {
          break
        }
        const priceForChild =
          category.valueType === childrenCategoryDerivationType.PERCENTAGE
            ? (+category.percentageValue / 100) * priceForAdult
            : +category.nominalValue
        priceForChildren += priceForChild
        break
      }

      case childrenCategoriesType.MANUAL:
        priceForChildren += category.nominalValue
        break
    }
  })

  return priceForChildren
}

export default getFirstNightPrice
